import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Users, Award, Calendar, CheckCircle, TrendingUp, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getServices, getProjects, getTestimonials, getStats } from '../services/api';
import { services as mockServices, projects as mockProjects, testimonials as mockTestimonials, stats as mockStats } from '../mockData';

export const Home = () => {
  const [services, setServices] = useState(mockServices);
  const [projects, setProjects] = useState(mockProjects);
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [stats, setStats] = useState(mockStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, projectsData, testimonialsData, statsData] = await Promise.all([
          getServices(),
          getProjects(),
          getTestimonials(),
          getStats()
        ]);
        
        if (servicesData.length > 0) setServices(servicesData);
        if (projectsData.length > 0) setProjects(projectsData);
        if (testimonialsData.length > 0) setTestimonials(testimonialsData);
        if (statsData.length > 0) setStats(statsData);
      } catch (error) {
        console.log('Using mock data:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-blue-50 to-white -z-10"></div>
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block">
                <span className="!font-bold !text-lg px-6 py-3 rounded-full text-orange-700 bg-orange-100">Leading Solar EPC Company in India

                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Smart Solar Solutions for{' '}
                <span className="text-orange-500">
                  a Sustainable Tomorrow
                </span>
              </h1>
              <p className="!text-xl text-gray-600">End-to-end <strong>solar EPC solutions</strong> for <strong>residential, commercial, and industrial</strong> consumers, delivered with strong engineering, quality materials, and execution discipline. Designed to deliver <strong>maximum energy generation, reliable performance, and faster returns</strong>.




              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/calculator">
                  <Button data-testid="calculate-savings-btn" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                    Calculate Savings
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button data-testid="schedule-visit-btn" variant="outline" className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg rounded-xl font-semibold transition-all duration-300">
                    Schedule a Free Site Visit
                  </Button>
                </Link>
              </div>
              <div className="mt-6">
                <p className="text-gray-700 font-semibold text-lg">
                  65+ MW of solar projects executed across India for leading industries, institutions, and MSMEs
                </p>
              </div>
            </div>
            <div className="relative">
              <video 
                autoPlay 
                loop 
                muted 
                playsInline
                className="rounded-2xl shadow-2xl w-full h-auto"
                style={{ maxHeight: '600px', objectFit: 'cover' }}
              >
                <source src="/videos/solarbull_drone.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <p className="text-3xl font-bold text-orange-600">65+ MW</p>
                <p className="text-gray-600 font-medium">Installed Capacity</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-8">
            <p className="text-2xl font-bold text-gray-800">
              Rooftop Solar • Commercial & Industrial Solar • Open Access Solar • PM-KUSUM Projects
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8" data-testid="stats-section">
            {stats.map((stat, index) =>
            <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:scale-105">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl mb-4">
                    {stat.icon === 'zap' && <Zap className="h-7 w-7 text-white" />}
                    {stat.icon === 'users' && <Users className="h-7 w-7 text-white" />}
                    {stat.icon === 'award' && <Award className="h-7 w-7 text-white" />}
                    {stat.icon === 'calendar' && <Calendar className="h-7 w-7 text-white" />}
                  </div>
                  <h3 className="text-4xl font-bold text-gray-900 mb-2">{stat.value}+</h3>
                  <p className="text-gray-600 font-medium">{stat.label}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Environmental Impact Counter */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-700 to-blue-800 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold mb-3">Our Environmental Impact</h2>
            <p className="text-blue-100 text-lg">Every solar project we install contributes to a cleaner planet</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center bg-white/10 border border-orange-400/30 rounded-2xl p-8 hover:bg-orange-500/20 transition-all duration-300">
              <div className="text-5xl mb-3">🌳</div>
              <p className="text-4xl font-bold text-orange-400 mb-2">19.5 Lakh+</p>
              <p className="text-white font-medium">Trees Equivalent</p>
              <p className="text-blue-200 text-sm mt-1">CO₂ offset over 25 years</p>
            </div>
            <div className="text-center bg-white/10 border border-orange-400/30 rounded-2xl p-8 hover:bg-orange-500/20 transition-all duration-300">
              <div className="text-5xl mb-3">⚡</div>
              <p className="text-4xl font-bold text-orange-400 mb-2">240 Cr+</p>
              <p className="text-white font-medium">Units Generated</p>
              <p className="text-blue-200 text-sm mt-1">Clean electricity over 25 years</p>
            </div>
            <div className="text-center bg-white/10 border border-orange-400/30 rounded-2xl p-8 hover:bg-orange-500/20 transition-all duration-300">
              <div className="text-5xl mb-3">💨</div>
              <p className="text-4xl font-bold text-orange-400 mb-2">1.95 Lakh+</p>
              <p className="text-white font-medium">Tonnes CO₂ Avoided</p>
              <p className="text-blue-200 text-sm mt-1">Equivalent carbon offset</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Our <span className="text-orange-500">Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive solar solutions tailored to your energy needs
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="services-section">
            {services.slice(0, 6).map((service) =>
            <Card key={service.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Zap className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3 text-gray-900">{service.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                  <div className="space-y-2 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) =>
                  <div key={idx} className="flex items-center text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-orange-500 mr-2" />
                        {feature}
                      </div>
                  )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button data-testid="view-all-services-btn" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                Why Choose <span className="text-orange-500">SolarBull</span>
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                We are committed to delivering excellence in every solar project, ensuring maximum value and satisfaction for our customers.
              </p>
              <div className="space-y-6">
                {[
                { icon: ShieldCheck, title: 'Premium Quality', desc: 'Only tier-1 solar panels with 30-year warranty' },
                { icon: TrendingUp, title: 'Maximum Savings', desc: 'Up to 90% reduction in electricity bills' },
                { icon: Award, title: 'Expert Team', desc: '9+ years of experience, 150+ successful projects' },
                { icon: Users, title: 'After-Sales Support', desc: 'Remote Monitoring, Preventive Maintenance to Comprehensive O&M Services' }].
                map((item, index) =>
                <div key={index} className="flex items-start space-x-4 group">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500 transition-colors duration-300">
                      <item.icon className="h-6 w-6 text-orange-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-1">{item.title}</h4>
                      <p className="text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/pphoto.jpg"
                alt="Solar Projects"
                className="rounded-2xl shadow-2xl"
              />

            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              Featured <span className="text-orange-500">Projects</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Showcasing our successful solar installations
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="projects-section">
            {projects.slice(0, 3).map((project) =>
            <Card key={project.id} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500" />

                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-orange-600">
                    {project.capacity}
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-gray-900">{project.title}</h3>
                  <p className="text-gray-600 mb-4">{project.description}</p>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{project.location}</span>
                    <span className="text-orange-600 font-semibold">{project.savings_per_year || project.savingsPerYear}/year</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          <div className="text-center mt-12">
            <Link to="/projects">
              <Button data-testid="view-all-projects-btn" className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300">
                View All Projects
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              What Our <span className="text-orange-500">Customers Say</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real feedback from satisfied customers across Telangana
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="testimonials-section">
            {testimonials.slice(0, 3).map((testimonial) =>
            <Card key={testimonial.id} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <svg key={i} className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                  )}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full mr-4" />
                      <div>
                        <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                        <p className="text-sm text-gray-600">{testimonial.designation}, {testimonial.location}</p>
                      </div>
                    </div>
                    {testimonial.source &&
                  <div className="text-xs text-blue-600 font-semibold bg-blue-50 px-3 py-1 rounded-full">
                        {testimonial.source}
                      </div>
                  }
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Switch to Solar?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get a free consultation and quote for your solar installation. Start saving on electricity bills today!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/calculator">
              <Button data-testid="cta-calculate-savings-btn" className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105">
                Calculate Your Savings
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button data-testid="cta-contact-btn" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-orange-600 px-8 py-6 text-lg rounded-xl font-semibold transition-all duration-300">
                Contact Us Today
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};
